import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Admin from './components/Admin';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Products from './components/Products';
import Service from './components/Service';
import QnA from './components/QnA';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Privacy from './components/Privacy';
import NoticePopup from './components/NoticePopup';
import Home from './components/Home';
import Pending from './components/Pending';
import Greeting from './components/about/Greeting';
import History from './components/about/History';
import Location from './components/about/Location';
import SolIntro from './components/solution/SolIntro';
import SolStructure from './components/solution/SolStructure';
import SolFeatures from './components/solution/SolFeatures';
import Press from './components/news/Press';
import Media from './components/news/Media';
import Faq from './components/support/Faq';
import Inquiry from './components/support/Inquiry';
import NoticePageNew from './components/support/Notice';
import { MENU } from './components/SiteHeader';
import { LanguageProvider } from './LanguageContext';
import './home-tokens.css';
import './App.css';

// 2026-09-30 리뉴얼 시안 이식 — 메인만 먼저 교체.
// 되돌리려면 false 로 두면 기존 풀페이지 메인(MainSite)이 그대로 뜬다.
const USE_NEW_HOME = true;

// 이미 실제 페이지가 있는 경로 — 여기에 추가하면 '준비중' 자동연결에서 제외된다
const BUILT_ROUTES = [
  '/notice', '/privacy',
  '/about/greeting', '/about/history', '/about/location',
  '/solution/intro', '/solution/structure', '/solution/features',
  '/news/press', '/news/media',
  '/support/faq', '/support/inquiry',
];

const SECTION_IDS = ['hero', 'about', 'products', 'service', 'qna', 'contact'];

// 라우터는 경로가 바뀌어도 스크롤을 안 건드린다.
// 메인 중간에서 메뉴를 누르면 서브페이지가 그 위치 그대로 열려 어색했다.
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin-medi2025" element={<Admin />} />
          <Route path="/notice" element={<NoticePageNew />} />
          <Route path="/about/greeting" element={<Greeting />} />
          <Route path="/about/history" element={<History />} />
          <Route path="/about/location" element={<Location />} />
          <Route path="/solution/intro" element={<SolIntro />} />
          <Route path="/solution/structure" element={<SolStructure />} />
          <Route path="/solution/features" element={<SolFeatures />} />
          <Route path="/news/press" element={<Press />} />
          <Route path="/news/media" element={<Media />} />
          <Route path="/support/faq" element={<Faq />} />
          <Route path="/support/inquiry" element={<Inquiry />} />
          {/* 헤더 MENU 에 있는 경로 중 아직 페이지가 없는 것은 준비중으로 연결.
              실제 페이지를 만들면 이 자동 생성 목록에서 빠진다. */}
          {MENU.flatMap((m) =>
            m.items
              .filter((it) => !BUILT_ROUTES.includes(it.to))
              .map((it) => (
                <Route
                  key={it.to}
                  path={it.to}
                  element={<Pending crumb={m.label} title={it.label} />}
                />
              ))
          )}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={USE_NEW_HOME ? <Home /> : <MainSite />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}


function MainSite() {
  const [currentSection, setCurrentSection] = useState(0);
  const isScrolling = useRef(false);
  const containerRef = useRef(null);
  const touchStartY = useRef(0);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);

    // 다른 페이지에서 넘어온 경우
    const scrollTarget = sessionStorage.getItem('scrollTo');
    if (scrollTarget) {
      sessionStorage.removeItem('scrollTo');
      const index = SECTION_IDS.indexOf(scrollTarget);
      if (index >= 0) {
        setTimeout(() => {
          const el = document.getElementById(scrollTarget);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
            setCurrentSection(index);
          }
        }, 300);
      }
    }
  }, []);

  const lastScrollTime = useRef(0);

  const scrollToSection = useCallback((index) => {
    if (index < 0 || index >= SECTION_IDS.length || isScrolling.current) return;
    isScrolling.current = true;
    setCurrentSection(index);

    const el = document.getElementById(SECTION_IDS[index]);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }

    setTimeout(() => {
      isScrolling.current = false;
    }, 500);
  }, []);

  useEffect(() => {
    // 모바일에선 페이지 스냅 비활성화 — 일반 스크롤로 동작
    const isMobile = () => window.matchMedia('(max-width: 900px)').matches || ('ontouchstart' in window && window.innerWidth < 900);
    if (isMobile()) {
      return; // wheel/touch/keyboard 핸들러 등록 안 함
    }

    const handleWheel = (e) => {
      const now = Date.now();
      if (isScrolling.current || now - lastScrollTime.current < 500) {
        e.preventDefault();
        return;
      }

      const isLast = currentSection >= SECTION_IDS.length - 1;

      // 마지막 섹션에서 아래로 → 자연 스크롤로 푸터 보기
      if (isLast && e.deltaY > 0) {
        return;
      }

      // 푸터가 보이는 상태에서 위로 → 마지막 섹션으로 스냅
      var footerEl = document.querySelector('.footer');
      var footerVisible = footerEl && footerEl.getBoundingClientRect().top < window.innerHeight;
      if (isLast && e.deltaY < 0 && footerVisible) {
        e.preventDefault();
        lastScrollTime.current = now;
        isScrolling.current = true;
        var el = document.getElementById(SECTION_IDS[SECTION_IDS.length - 1]);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
        setTimeout(function() { isScrolling.current = false; }, 500);
        return;
      }

      e.preventDefault();
      lastScrollTime.current = now;
      if (e.deltaY > 0) {
        scrollToSection(currentSection + 1);
      } else {
        scrollToSection(currentSection - 1);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        scrollToSection(currentSection + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        scrollToSection(currentSection - 1);
      }
    };

    const handleTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      if (isScrolling.current) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          scrollToSection(currentSection + 1);
        } else {
          scrollToSection(currentSection - 1);
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentSection, scrollToSection]);

  return (
    <div className="App" ref={containerRef}>
      <Navbar currentSection={currentSection} onNavigate={scrollToSection} sectionIds={SECTION_IDS} />
      <div className="fullpage-section" id="hero"><Hero /></div>
      <div className="fullpage-section" id="about"><About /></div>
      <div className="fullpage-section" id="products"><Products /></div>
      <div className="fullpage-section" id="service"><Service /></div>
      <div className="fullpage-section" id="qna"><QnA /></div>
      <div className="fullpage-section fullpage-last" id="contact">
        <Contact />
        <Footer />
      </div>
      <div className="section-dots">
        {SECTION_IDS.map((id, i) => (
          <button
            key={id}
            className={`dot ${currentSection === i ? 'active' : ''}`}
            onClick={() => scrollToSection(i)}
            aria-label={id}
          />
        ))}
      </div>
      <NoticePopup />
    </div>
  );
}

export default App;
